import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { apiForgotPassword, formatApiError } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await apiForgotPassword(email);
      setSubmitted(true);
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <section className="pt-16 pb-20 max-w-md mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="border border-neutral-900 bg-neutral-950/60 p-7"
        data-testid="forgot-password-page"
      >
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#00E5FF] mb-3">
          ── account recovery ──
        </div>
        <h1 className="text-3xl font-black tracking-tight text-white">Forgot password</h1>

        {submitted ? (
          <div className="mt-6" data-testid="forgot-success">
            <div className="flex items-center gap-2 text-sm text-[#00FF66] mb-3">
              <CheckCircle2 className="w-4 h-4" /> Check your inbox
            </div>
            <p className="text-sm text-neutral-400 leading-relaxed">
              If that email is registered, a password reset link has been sent.
              The link expires in 1 hour.
            </p>
            <p className="text-xs text-neutral-600 mt-3">
              Didn't get the email? Check spam, or try again in a few minutes.
            </p>
            <Link
              to="/login"
              data-testid="back-to-login"
              className="mt-6 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.25em] text-neutral-400 hover:text-white"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> back to login
            </Link>
          </div>
        ) : (
          <>
            <p className="text-sm text-neutral-400 mt-1.5 mb-6">
              Enter your email and we'll send a reset link.
            </p>
            <form onSubmit={submit} className="space-y-3" data-testid="forgot-form">
              <label className="flex items-center gap-2 border border-neutral-800 bg-neutral-950 px-3.5 focus-within:border-neutral-600 transition-colors">
                <Mail className="w-4 h-4 text-neutral-500" />
                <input
                  data-testid="forgot-email"
                  type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com" autoComplete="email"
                  className="flex-1 bg-transparent border-0 outline-none py-3 text-sm text-white placeholder:text-neutral-600"
                />
              </label>
              {err && <div className="text-xs text-red-400" data-testid="forgot-error">{err}</div>}
              <button
                type="submit" disabled={busy} data-testid="forgot-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-white text-black font-bold text-sm uppercase tracking-wider px-6 py-3 hover:bg-neutral-200 transition-colors disabled:opacity-50"
              >
                {busy ? "Sending…" : "Send reset link"}
              </button>
            </form>
            <div className="mt-5 text-xs text-neutral-500 text-center">
              <Link to="/login" data-testid="forgot-back" className="text-neutral-400 hover:text-white">
                ← back to login
              </Link>
            </div>
          </>
        )}
      </motion.div>
    </section>
  );
}
